import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { goToCoachProfile } from '../utils/coachNav';
import { useAuth } from '../contexts/AuthContext';
import type { Lesson } from '../types/lesson';
import type { Order } from '../types/order';
import { formatOfferingLabel } from '../utils/offeringLabel';
import type { CoachOffering } from '../types/lesson';
import { formatPrice } from '../utils/formatPrice';
import { LessonEditPreview } from './LessonEditPreview';
import LessonReviewBlock from './LessonReviewBlock';
import { AttachedDrills } from './AttachedDrills';
import BackButton from './BackButton';
import { getActiveCoachOfferings } from '../services/coachOfferings';
import { addLessonReview, markLessonViewed } from '../services/lessons';
import { getReviewByLessonId, getReviewByOrderId } from '../services/reviews';
import { useIsMobile } from '../contexts/ResponsiveContext';
import { useStorageDownloadUrl } from '../hooks/useStorageDownloadUrl';
import ReviewModal from './ReviewModal';
import { useUserIdentity } from '../hooks/useUserIdentity';
import { lessonTitle } from './PlayerLessonCard';
import DetailSection from './DetailSection';
import { BTN_PRIMARY, BTN_SECONDARY_ON_PAGE } from './buttonStyles';
import { formatLessonDate } from '../utils/lessonDate';
import GuaranteeBlock from './GuaranteeBlock';

interface LessonDeliveredDetailProps {
  order: Order;
  lesson: Lesson;
  onBack?: () => void;
  /** @deprecated Message CTA was removed from this screen so the program
   *  upsell stays the only conversion path. Kept in the interface so existing
   *  call sites (`LessonOrderPanel`, etc.) compile unchanged. Wire to nothing. */
  onMessage?: () => void;
  /** Hides the "Continue with Coach X?" cross-sell block when the player
   *  already has an active training-program order with THIS coach. Computed
   *  by the parent against the player's order list. Completed-program case
   *  is intentionally NOT suppressed (re-engagement is desired) — predicate
   *  upstream should match the existing `hasActiveProgram` shape: same coach,
   *  training_program, !refundRequested, computeOrderStatus === 'active'. */
  hasActiveProgramWithCoach?: boolean;
  /** 'page' (default) is the full-screen route treatment: a 760px centred
   *  column on the grey app background, with its own back button. 'panel'
   *  fills a two-pane container instead — full width, white, no back button,
   *  since the list beside it is already the way back. Padding is identical
   *  in both. */
  variant?: 'page' | 'panel';
  /** Program-lesson hosting — BundleLessonDetail renders this screen for a
   *  delivered program lesson. Presence switches three things: the title
   *  becomes "Lesson N of M"; the review is one per PROGRAM (looked up by
   *  order, allowed only when `reviewEligible`, and the prompt card's button
   *  is leave-review-btn — rate-lesson-btn never renders on a program
   *  lesson); and the program upsell is dropped (the player is already in
   *  one). Everything else — coach row, submission, breakdown, notes,
   *  drills — renders exactly as the single-lesson screen. */
  program?: {
    lessonNumber: number;
    totalLessons: number;
    /** Lesson 2 or later AND the order's most recently delivered lesson. */
    reviewEligible: boolean;
  };
  /** Rendered inside the content column after the review footer — the
   *  program wrapper's upload CTA and progress-report banner. Layout only;
   *  the handlers stay with the wrapper that owns them. */
  footerExtras?: ReactNode;
}

export default function LessonDeliveredDetail({ order, lesson, onBack, hasActiveProgramWithCoach = false, variant = 'page', program, footerExtras }: LessonDeliveredDetailProps) {
  const isPanel = variant === 'panel';
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [coachOfferings, setCoachOfferings] = useState<CoachOffering[]>([]);
  const [showReview, setShowReview] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  // Per-lesson dismissal of the review prompt. localStorage keyed by lesson
  // id, the same pattern as the visit counter below and as
  // services/requestLessonDraft.ts. Read lazily so the row never flashes on
  // before it hides. No Firestore write.
  const reviewPromptDismissKey = `lesson_review_prompt_dismissed_${lesson.id}`;
  const [reviewPromptDismissed, setReviewPromptDismissed] = useState<boolean>(() => {
    try { return localStorage.getItem(reviewPromptDismissKey) === '1'; }
    catch { return false; }
  });
  const dismissReviewPrompt = () => {
    setReviewPromptDismissed(true);
    try { localStorage.setItem(reviewPromptDismissKey, '1'); } catch { /* quota / private browsing */ }
  };
  const isMobile = useIsMobile();

  // Stable identity across renders. Passed to LessonEditPreview, whose load
  // effect keys on this object — an inline literal here re-fired that effect on
  // every parent re-render, tearing the player down to its loading spinner.
  const previewContext = useMemo(
    () => ({ type: 'lesson' as const, id: lesson.id }),
    [lesson.id],
  );

  // Poster for the submission video. Frame 0 of the preprocessor's thumbnail
  // strip when the lesson has one (same path shape as PlayerLessonCard's
  // firstFramePath); lessons uploaded before thumbnail generation fall back
  // to a #t=0.001 media fragment on the src, which is what makes iOS Safari
  // paint the first frame instead of a black box.
  const submissionThumbPath =
    lesson.playerVideoThumbnailsBasePath && (lesson.playerVideoThumbnailCount ?? 0) > 0
      ? `${lesson.playerVideoThumbnailsBasePath}/0.jpg`
      : null;
  const submissionPoster = useStorageDownloadUrl(submissionThumbPath);
  const submissionSrc = lesson.playerVideoUrl
    ? (submissionPoster ? lesson.playerVideoUrl : `${lesson.playerVideoUrl}#t=0.001`)
    : undefined;

  // Whether the breakdown is playing. Drives the BREAKDOWN badge overlay:
  // visible only while paused, so it never sits over the coach's camera
  // bubble during playback.
  const [breakdownPlaying, setBreakdownPlaying] = useState(false);

  useEffect(() => {
    if (order.coachId) {
      getActiveCoachOfferings(order.coachId).then(setCoachOfferings).catch(() => {});
    }
  }, [order.coachId]);

  useEffect(() => {
    // One review per PROGRAM on a program lesson: the lookup keys on the
    // order, so a review left from any lesson hides the prompt on all of
    // them. Single lessons stay per-lesson.
    (program ? getReviewByOrderId(order.id) : getReviewByLessonId(lesson.id))
      .then(r => { if (r) setHasReviewed(true); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson.id, order.id, !!program]);

  // Mark delivered lesson as viewed by the player
  useEffect(() => {
    if (lesson.status === 'delivered' && currentUser?.uid === lesson.playerId && !lesson.playerViewedAt) {
      markLessonViewed(lesson.id).catch(console.error);
    }
  }, [lesson.id, lesson.status, lesson.playerId, lesson.playerViewedAt, currentUser?.uid]);

  const bookOffering = (type: 'single_lesson' | 'training_program') => {
    const offering = coachOfferings.find(o => o.offeringType === type);
    if (offering) {
      navigate(`/request-lesson/${order.coachId}/${offering.id}`);
    }
  };

  // Live identity overrides the order's denormalized coachName/Avatar
  // snapshot so this screen shows the coach's current name + photo even
  // after a rename. Null while loading; falls back to the snapshot.
  const liveCoach = useUserIdentity(order.coachId, 'coach');
  const coachName = liveCoach?.name ?? order.coachName ?? '';
  const coachAvatarUrl = liveCoach?.avatarUrl ?? order.coachAvatarUrl;
  const coachFirstName = coachName.split(' ')[0] || 'your coach';
  const coachInitials = (coachName || 'C')
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const isConsult = order.type === 'shot_consult';
  const coachId = lesson.coachId ?? order.coachId;
  const deliveredDate = lesson.deliveredAt
    ? formatLessonDate(lesson.deliveredAt)
    : '';

  /* Program lessons title by their place in the program; everything else
     keeps the product name the lesson list shows. */
  const headerTitle = program
    ? `Lesson ${program.lessonNumber} of ${program.totalLessons}`
    : lessonTitle(lesson, order);


  // ── Program upsell data — pulled from the coach's actual offerings.
  //    Shown specifics: total lessons, per-lesson price, and per-lesson
  //    savings vs the same coach's single-lesson offering when programs
  //    are priced cheaper per lesson. Silently omits any line whose data
  //    isn't loaded yet.
  const programOffering = coachOfferings.find(o => o.offeringType === 'training_program');
  const programTotalLessons = programOffering?.totalLessons ?? 0;
  const programPerLessonCents = programOffering?.price ?? 0;

  // ── Shared blocks — both branches render the identical markup ─────────────
  // Plain values, not components, so nothing remounts between renders.

  // Same treatment as the player's own note on the waiting screen, so the two
  // halves of the exchange read as a pair.
  const notesSection = lesson.coachNotes?.trim() ? (
    <DetailSection className="mb-10" header={`${coachFirstName}'s notes`}>
      <p className="text-[15px] leading-[20px] text-[#1C1C1C] whitespace-pre-wrap">
        {lesson.coachNotes}
      </p>
    </DetailSection>
  ) : null;

  // Total, not per lesson: CoachOffering carries only `price` (per lesson) and
  // `totalLessons`, and there is no order for a program the player hasn't
  // bought yet, so the total has to be multiplied out. Same arithmetic as
  // orderPriceDisplay() in PlayerLessonCard.
  const programMetaLine = programTotalLessons > 0
    ? `${programTotalLessons} lessons${programPerLessonCents > 0 ? ` · ${formatPrice(programPerLessonCents * programTotalLessons)}` : ''}`
    : '';

  const upsellBullets = [
    'Four full video lessons',
    'Each lesson builds on the last',
    'A progress note at the end',
  ];

  /* Never on a program lesson — the player is already in a program. */
  const upsellCard = !program && programOffering && !hasActiveProgramWithCoach ? (
    <div className="mb-8">
      {/* One surface, no internal hairlines. It was four bands split by rules:
          coach header, body, price strip, refund line. The title moves out to
          the section header, so the row inside is just identity. */}
      <DetailSection header="Keep the momentum going">
        <div className="flex items-center" style={{ gap: 12 }}>
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden text-[13px] font-bold text-white flex-shrink-0"
            style={{ background: '#54C3EA' }}
          >
            {coachAvatarUrl
              ? <img src={coachAvatarUrl} alt={coachName} className="w-full h-full object-cover" />
              : coachInitials}
          </div>
          <div className="min-w-0 text-[15px] leading-[20px] font-semibold text-[#1C1C1C] truncate">
            {coachName} · Training Program
          </div>
        </div>

        <p className="text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]" style={{ marginTop: 16 }}>
          In a program, {coachFirstName} sees the full picture of your game and builds a plan that changes as you improve.
        </p>

        <div className="flex flex-col" style={{ marginTop: 16, gap: 10 }}>
          {upsellBullets.map((b) => (
            <div key={b} className="flex text-[13px] leading-[18px] text-[#1C1C1C]" style={{ gap: 12 }}>
              {/* Biscayne, already CL-1's brand value — see the report. */}
              <span
                className="rounded-full flex-shrink-0"
                style={{ width: 5, height: 5, background: '#1A6B8A', marginTop: 'calc(0.9em - 2.5px)' }}
              />
              <span>{b}</span>
            </div>
          ))}
        </div>

        {programMetaLine && (
          <div className="text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]" style={{ marginTop: 16 }}>
            {programMetaLine}
          </div>
        )}

        <button
          onClick={() => bookOffering('training_program')}
          className={`${BTN_PRIMARY} w-full`}
          style={{ marginTop: 16 }}
        >
          Start Program
        </button>
      </DetailSection>

      {/* Below the section, on the page background: the promise covers the
          purchase, not one row of it. */}
      <GuaranteeBlock className="mt-4" />
    </div>
  ) : null;

  // One boolean drives both placements, so they cannot both appear. The banner
  // is the stronger of the two; the footer is what remains once it is
  // dismissed.
  /* On a program lesson the review is gated by the wrapper's program rules
     (lesson ≥ 2, most recent delivered). Ineligible lessons render no review
     UI at all — neither the card nor the footer. */
  const reviewAllowed = !program || program.reviewEligible;
  const showReviewBanner = reviewAllowed && !hasReviewed && !reviewPromptDismissed;

  const reviewPromptTop = showReviewBanner ? (
    <DetailSection className="mb-7">
      <div className="flex items-center justify-between" style={{ gap: 16 }}>
      {/* items-center, not items-start: with one line of text on mobile the row
          has nothing to top-align to. The star loses its mt-0.5 nudge for the
          same reason; on desktop the second line returns and the group is
          still centred against it. */}
      <div className="flex items-center min-w-0" style={{ gap: 12 }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="#1A6B8A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 w-[18px] h-[18px] md:w-5 md:h-5" aria-hidden="true">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        <div className="min-w-0">
          <div className="text-[15px] leading-[20px] font-semibold text-[#1C1C1C]">How was your lesson?</div>
          {/* Mobile drops to a single row, so the explanatory line goes. The
              button beside it already says what the action is. */}
          <div className="hidden md:block text-[13px] leading-[18px] text-[rgba(60,60,67,0.6)]">Leave {coachFirstName} a rating and a short review</div>
        </div>
      </div>
      {/* Grouped so space-between has exactly two children: the text block
          and these. Otherwise the free space splits three ways and the button
          lands mid-row instead of at the right edge. */}
      <div className="flex items-center flex-shrink-0" style={{ gap: 16 }}>
        <button
          /* leave-review-btn on program lessons: the program specs address
             the review through that id, visible on open. rate-lesson-btn
             never renders on a program lesson. */
          data-testid={program ? 'leave-review-btn' : 'rate-lesson-btn'}
          onClick={() => setShowReview(true)}
          // 32 inline on mobile so the row fits on one line; 48 on desktop,
          // where it is unchanged.
          className={`${BTN_PRIMARY} flex-shrink-0 h-8 md:h-12 font-medium md:font-medium text-[13px] leading-[18px] md:text-[17px] md:leading-[22px] px-3 md:px-5 w-auto`}
        >
          Rate Lesson
        </button>
        <button
          onClick={dismissReviewPrompt}
          aria-label="Dismiss"
          className="text-[#8A94A0] hover:text-[#1C1C1C] bg-transparent border-none cursor-pointer flex-shrink-0 transition-colors"
          style={{ padding: 4 }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      </div>
    </DetailSection>
  ) : null;

  /**
   * The fallback way in, for a player who dismissed the prompt at the top and
   * has not reviewed. Nothing else: it used to carry "You rated this lesson 5
   * of 5" for the reviewed case, which restated the card at the top of the
   * screen — the same stars and the same date — as a bare grey line with no
   * section around it.
   *
   * Renders nothing at all rather than an empty box, so a reviewed lesson
   * loses the 40px of bottom padding that only existed to sit under it.
   */
  const reviewFooter = reviewAllowed && !hasReviewed && !showReviewBanner ? (
    <div className="pb-10">
      <button
        data-testid="leave-review-btn"
        onClick={() => setShowReview(true)}
        className={`${BTN_SECONDARY_ON_PAGE} w-full`}
      >
        Rate This Lesson
      </button>
    </div>
  ) : null;


  // ── SHOT CONSULT LAYOUT (restructured) ──────────────────────────────────
  // Hierarchy: back → compact header (small circle avatar, no pill) →
  // collapsed submission disclosure → breakdown hero → notes companion →
  // contained cross-sell card. Programlesson + single-lesson detail
  // screens are explicitly out of scope and use the unchanged layout below.
  if (isConsult) {
    const hasSubmission = !!lesson.playerVideoUrl;
    const hasBreakdown = !!(lesson.publishedDraftId && lesson.publishedOriginalVideoId);

    return (
      <div className={`flex flex-col h-full overflow-y-auto${isPanel ? '' : ' bg-db-bg'}`}>
        <div className={`p-6 md:p-8 pt-5 md:pt-8 w-full mx-auto ${isPanel ? 'max-w-[860px]' : 'max-w-[760px]'} ${isMobile ? 'pb-[calc(32px+env(safe-area-inset-bottom,0px))]' : ''}`}>

          {/* Header — matches the waiting screen: product name as the title,
              coach identity demoted to the meta row beneath it. Back sits
              inline with the title; the panel variant needs neither. */}
          <div className={`${isPanel ? '' : 'sticky top-0 z-30 bg-db-bg'} flex items-center gap-3 py-2`}>
            {!isPanel && <BackButton onClick={onBack} fallback="/lessons" flush />}
            <h1 className="text-[26px] font-bold text-[#1C1C1C]">{headerTitle}</h1>
          </div>

          {/* The review, first thing in the content column. */}
          <LessonReviewBlock lessonId={lesson.id} viewer="player" className="mb-7" />


          <div className="flex items-center gap-3 min-w-0 mt-4 mb-7">
            <button
              type="button"
              title={coachName || 'Coach'}
              onClick={() => goToCoachProfile(coachId, currentUser?.uid ?? undefined)}
              className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden text-[16px] font-bold text-white flex-shrink-0 border-none p-0 cursor-pointer"
              style={{ background: '#54C3EA' }}
            >
              {coachAvatarUrl
                ? <img src={coachAvatarUrl} alt={coachName} className="w-full h-full object-cover" />
                : coachInitials}
            </button>
            {/* Name and date wrap as one group, so the date drops to a second
                line only when the name is long enough to push it. Either way
                that line starts under the name, not under the avatar. */}
            <div className="min-w-0 flex flex-wrap items-center gap-x-3">
              <span className="text-[14px] text-[#1C1C1C]">{coachName}</span>
              {deliveredDate && (
                <span className="text-[14px] text-db-text-2 whitespace-nowrap">· Delivered {deliveredDate}</span>
              )}
            </div>
          </div>

          {reviewPromptTop}

          {/* Submission — full inline native HTML5 video, parity with the
              breakdown player below. Same italic-muted section label and
              same player frame so the two surfaces read as peers. */}
          {hasSubmission && (
            <div className="mb-6">
              <p className="text-[13px] leading-[18px] font-semibold uppercase text-[rgba(60,60,67,0.6)]" style={{ letterSpacing: '0.5px', marginBottom: 16 }}>Your submission</p>
              <video
                src={submissionSrc}
                poster={submissionPoster ?? undefined}
                controls
                controlsList="nodownload"
                preload="metadata"
                className="w-full rounded-[14px] bg-black"
                style={{ maxHeight: '70vh' }}
                /* No crossOrigin: nothing reads this element's pixels, and the
                   attribute forces CORS-mode media loading, which iPhone
                   Safari's loader rejects against our bucket — its CORS config
                   does not expose Content-Range, so range responses are
                   unreadable and iOS shows the crossed-out play glyph. A
                   plain media load has no such requirement. */
              />
            </div>
          )}

          {/* Breakdown — hero of the page. Section label uses the same
              italic-muted treatment as other section labels. Tight `mb-5`
              so the notes companion reads as the immediate pair, not a
              separate section. */}
          {hasBreakdown && (
            <div className="mb-5">
              <p className="text-[13px] leading-[18px] font-semibold uppercase text-[rgba(60,60,67,0.6)]" style={{ letterSpacing: '0.5px', marginBottom: 16 }}>{coachFirstName}&apos;s breakdown</p>
              <div className="relative w-full">
                <LessonEditPreview
                  offeringLabel={order ? formatOfferingLabel({ offeringType: order.type, lessonNumber: lesson.lessonNumber, totalLessons: order.totalLessons }) : undefined}
                  deliveredAt={lesson.deliveredAt}
                  context={previewContext}
                  originalVideoId={lesson.publishedOriginalVideoId!}
                  coachName={coachName}
                  draftId={lesson.publishedDraftId!}
                  showControls
                  downloadUrl={lesson.coachVideoUrl}
                  downloadStoragePath={lesson.coachVideoStoragePath}
                  onPlayingChange={setBreakdownPlaying}
                />
                {/* Overlay only — pointer-events:none so it never steals the
                    play control, and it sits outside the player, so it stays
                    up while the poster is showing. Top-RIGHT keeps it clear of
                    the player's own top-left overlay. Paused only: during
                    playback it would sit over the coach's camera bubble. */}
                {!breakdownPlaying && (
                <span
                  className="absolute flex items-center font-bold uppercase pointer-events-none z-[1] rounded-full"
                  style={{ top: 12, right: 12, gap: 6, fontSize: 12, letterSpacing: '.06em', padding: '5px 12px', background: '#54C3EA', color: '#091520' }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 2.5c0 4.5 2.5 7 7 7-4.5 0-7 2.5-7 7 0-4.5-2.5-7-7-7 4.5 0 7-2.5 7-7z" />
                  </svg>
                  Breakdown
                </span>
                )}
              </div>
            </div>
          )}

          {notesSection}

          {/* Attached drills — AttachedDrills self-styles its own cards, so
              it sits directly in the column with no wrapper chrome. */}
          {lesson.attachedDrills && lesson.attachedDrills.length > 0 && (
            <div className="mb-6">
              <AttachedDrills
                drills={lesson.attachedDrills}
                title={`${coachFirstName}'s Practice Drills`}
              />
            </div>
          )}

          {upsellCard}

          {reviewFooter}

        </div>

        {showReview && (
          <ReviewModal
            lesson={lesson}
            order={order}
            onSubmit={async (rating, comment) => {
              if (!currentUser) return;
              await addLessonReview(lesson.id, currentUser.uid, rating, comment);
              setHasReviewed(true);
              setShowReview(false);
            }}
            onSkip={() => setShowReview(false)}
          />
        )}
      </div>
    );
  }

  // ── SINGLE LESSON LAYOUT (unchanged from prior implementation) ───────────

  return (
    <div className={`flex flex-col h-full overflow-y-auto${isPanel ? '' : ' bg-db-bg'}`}>
      <div className={`p-6 md:p-8 pt-5 md:pt-8 w-full mx-auto ${isPanel ? 'max-w-[860px]' : 'max-w-[760px]'} ${isMobile ? 'pb-[calc(32px+env(safe-area-inset-bottom,0px))]' : ''}`}>

        {/* Header — matches the waiting screen: product name as the title,
            coach identity demoted to the meta row beneath it. Back sits inline
            with the title; the panel variant needs neither. */}
        <div className={`${isPanel ? '' : 'sticky top-0 z-30 bg-db-bg'} flex items-center gap-3 py-2`}>
          {!isPanel && <BackButton onClick={onBack} fallback="/lessons" flush />}
          <h1 className="text-[26px] font-bold text-[#1C1C1C]">{headerTitle}</h1>
        </div>

        {/* The review, first thing in the content column. */}
        <LessonReviewBlock lessonId={lesson.id} viewer="player" className="mb-7" />

        <div className="flex items-center gap-3 min-w-0 mt-4 mb-7">
          <button
            type="button"
            title={coachName || 'Coach'}
            onClick={() => goToCoachProfile(coachId, currentUser?.uid ?? undefined)}
            className="w-12 h-12 rounded-full flex items-center justify-center overflow-hidden text-[16px] font-bold text-white flex-shrink-0 border-none p-0 cursor-pointer"
            style={{ background: '#54C3EA' }}
          >
            {coachAvatarUrl
              ? <img src={coachAvatarUrl} alt={coachName} className="w-full h-full object-cover" />
              : coachInitials}
          </button>
          {/* Name and date wrap as one group, so the date drops to a second
              line only when the name is long enough to push it. Either way
              that line starts under the name, not under the avatar. */}
          <div className="min-w-0 flex flex-wrap items-center gap-x-3">
            <span className="text-[14px] text-[#1C1C1C]">{coachName}</span>
            {deliveredDate && (
              <span className="text-[14px] text-db-text-2 whitespace-nowrap">· Delivered {deliveredDate}</span>
            )}
          </div>
        </div>

        {reviewPromptTop}

        {/* Your submission — full inline native HTML5 video, parity with the
            coach screen's "Player's submission" treatment. */}
        {lesson.playerVideoUrl && (
          <div className="mb-6">
            <p className="text-[13px] leading-[18px] font-semibold uppercase text-[rgba(60,60,67,0.6)]" style={{ letterSpacing: '0.5px', marginBottom: 16 }}>Your submission</p>
            <video
              src={submissionSrc}
              poster={submissionPoster ?? undefined}
              controls
              controlsList="nodownload"
              preload="metadata"
              className="w-full rounded-[14px] bg-black"
              style={{ maxHeight: '70vh' }}
              /* No crossOrigin: nothing reads this element's pixels, and the
                 attribute forces CORS-mode media loading, which iPhone
                 Safari's loader rejects against our bucket — its CORS config
                 does not expose Content-Range, so range responses are
                 unreadable and iOS shows the crossed-out play glyph. A
                 plain media load has no such requirement. */
            />
          </div>
        )}

        {/* Coach's breakdown — full inline LessonEditPreview, parity with the
            coach screen's "Your breakdown" treatment. */}
        {lesson.publishedDraftId && lesson.publishedOriginalVideoId && (
          <div className="mb-6">
            <p className="text-[13px] leading-[18px] font-semibold uppercase text-[rgba(60,60,67,0.6)]" style={{ letterSpacing: '0.5px', marginBottom: 16 }}>{coachFirstName}&apos;s breakdown</p>
            <div className="relative w-full">
              <LessonEditPreview
                offeringLabel={order ? formatOfferingLabel({ offeringType: order.type, lessonNumber: lesson.lessonNumber, totalLessons: order.totalLessons }) : undefined}
                deliveredAt={lesson.deliveredAt}
                context={previewContext}
                originalVideoId={lesson.publishedOriginalVideoId}
                coachName={coachName}
                draftId={lesson.publishedDraftId}
                showControls
                downloadUrl={lesson.coachVideoUrl}
                downloadStoragePath={lesson.coachVideoStoragePath}
                onPlayingChange={setBreakdownPlaying}
              />
              {/* Overlay only — pointer-events:none so it never steals the
                  play control, and it sits outside the player, so it stays
                  up while the poster is showing. Top-RIGHT keeps it clear of
                  the player's own top-left overlay. Paused only: during
                  playback it would sit over the coach's camera bubble. */}
              {!breakdownPlaying && (
              <span
                className="absolute flex items-center font-bold uppercase pointer-events-none z-[1] rounded-full"
                style={{ top: 12, right: 12, gap: 6, fontSize: 12, letterSpacing: '.06em', padding: '5px 12px', background: '#54C3EA', color: '#091520' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 2.5c0 4.5 2.5 7 7 7-4.5 0-7 2.5-7 7 0-4.5-2.5-7-7-7 4.5 0 7-2.5 7-7z" />
                </svg>
                Breakdown
              </span>
              )}
            </div>
          </div>
        )}

        {notesSection}

        {/* Attached Drills — AttachedDrills self-styles its own cards, so it
            sits directly in the column with no wrapper chrome. */}
        {lesson.attachedDrills && lesson.attachedDrills.length > 0 && (
          <div className="mb-6">
            <AttachedDrills
              drills={lesson.attachedDrills}
              title={`${coachFirstName}'s Practice Drills`}
            />
          </div>
        )}

        {upsellCard}

        {reviewFooter}

        {/* Program wrapper's appendix — upload CTA and progress-report
            banner. Rendered inside the column so it scrolls with the body. */}
        {footerExtras}

      </div>

      {showReview && (
        <ReviewModal
          lesson={lesson}
          order={order}
          onSubmit={async (rating, comment) => {
            if (!currentUser) return;
            await addLessonReview(lesson.id, currentUser.uid, rating, comment);
            setHasReviewed(true);
            setShowReview(false);
          }}
          onSkip={() => setShowReview(false)}
        />
      )}
    </div>
  );
}
